export const databricksPagesData = {
    "databricks-consulting": {
        seo: {
            title: "Databricks Consulting Partner & Implementation | Mobikode",
            description:
                "Mobikode helps design, secure, and implement Databricks platforms, with governed data foundations and initial business use cases planned for production.",
            keywords:
                "Databricks consulting partner, Databricks implementation, Databricks consulting services, Databricks setup, Databricks experts",
            canonical:
                "https://www.mobikode.com/data-ai-databricks-solutions/databricks-consulting/",
            ogImage: "https://www.mobikode.com/assets/services/bg3.png",
        },
        hero: {
            partnerTitle: "Databricks Partner · Consulting & Implementation",
            headingLine1: "Databricks, done",
            headingLine2: "right the first time.",
            description:
                "Senior Databricks consultants design, secure, and launch your platform, with your first business use case planned for production in weeks.",
            ctaText: "Get a free Databricks assessment",
            ctaHref: "#databricks-lead-form",
            cardText:
                "Build a secure foundation for data engineering, analytics, and AI with an implementation plan shaped around your business priorities.",
            bgImageSrc: "/assets/datab/bg1.png",
        },
        foundation: {
            badge: "Start with the right foundation",
            title: "Turn your Databricks investment into a production-ready platform.",
            description:
                "A proof of concept can demonstrate potential. A platform that remains secure, governed, maintainable, and cost-aware at enterprise scale takes deliberate planning. Mobikode establishes the foundations first, then delivers agreed pipelines, dashboards, or models against real business requirements.",
            imageSrc: "/assets/datab/img1.png",
        },
        approach: {
            badge: "The Mobikode approach",
            heading: "Built for real-world data and AI delivery",
            items: [
                {
                    id: 1,
                    title: "Architecture Before Implementation",
                    desc: "We align your Databricks platform with business goals, cloud architecture, data sources, and long-term scale.",
                },
                {
                    id: 2,
                    title: "Security and Governance by Design",
                    desc: "Access policies, Unity Catalog, auditability, and environment separation are planned from the beginning.",
                },
                {
                    id: 3,
                    title: "Production-Ready Engineering",
                    desc: "We build repeatable deployments, tested pipelines, monitoring, and cost controls—not just a proof of concept.",
                },
                {
                    id: 4,
                    title: "From Strategy to Go-Live",
                    desc: "Our approach connects platform foundations, real business use cases, team enablement, and ongoing support.",
                },
            ],
        },
        deliverables: {
            badge: "Implementation scope",
            heading: "What we deliver",
            description:
                "The right platform components for your current priorities, with security, governance, deployment, and cost controls considered as part of the same implementation.",
            items: [
                {
                    id: 1,
                    title: "Platform Foundation",
                    desc: "Workspace and environment design for development, testing, and production, aligned with your cloud setup.",
                },
                {
                    id: 2,
                    title: "Security and Networking",
                    desc: "Plan identity and access, private connectivity, secrets, encryption, and audit logging for your requirements.",
                },
                {
                    id: 3,
                    title: "Unity Catalog Governance",
                    desc: "Set up centralized permissions, data discovery, lineage, and consistent governance standards.",
                },
                {
                    id: 4,
                    title: "Infrastructure as Code",
                    desc: "Use Terraform and Databricks Asset Bundles to make deployments repeatable and easier to maintain.",
                },
                {
                    id: 5,
                    title: "CI/CD and DataOps",
                    desc: "Establish automated testing and controlled promotion for data pipelines, jobs, and models.",
                },
                {
                    id: 6,
                    title: "Cost Guardrails",
                    desc: "Configure compute policies, tagging, budgets, and workload sizing to improve spend visibility.",
                },
            ],
        },
        deliveryPath: {
            badge: "From discovery to production",
            heading: "A clear path from platform design to go-live",
            subheading:
                "The schedule below is an indicative delivery approach. Final timing depends on scope, integrations, data readiness, and security review.",
            items: [
                {
                    step: "01",
                    duration: "Weeks 1–2",
                    title: "Discover",
                    desc: "We understand business priorities, data sources, cloud requirements, security expectations, and the use cases that matter most.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "02",
                    duration: "Weeks 2–3",
                    title: "Design",
                    desc: "We define the target architecture, workspace structure, governance approach, delivery plan, and success measures.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "03",
                    duration: "Weeks 4–8",
                    title: "Build",
                    desc: "We establish the platform foundations and develop the first agreed pipelines, dashboards, or model use cases in iterative sprints.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "04",
                    duration: "Weeks 8–12",
                    title: "Launch",
                    desc: "We validate quality and access, prepare monitoring and runbooks, support go-live, and address early production issues.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "05",
                    duration: "Ongoing",
                    title: "Enable or Operate",
                    desc: "Your team receives practical enablement and documentation, or Mobikode can continue supporting platform operations.",
                    image: "/assets/datab/img2.png",
                },
            ],
        },
        capabilities: {
            badge: "Designed around business value",
            heading: "What you can expect from a well-planned implementation",
            subheading:
                "The right platform components for your current priorities, with security, governance, deployment, and cost controls considered as part of the same implementation.",
            items: [
                {
                    id: 1,
                    title: "Earlier Production Value",
                    desc: "Prioritize one to three useful pipelines, dashboards, or models for the first release.",
                    image: "/assets/datab/item1.png",
                    cardBg: "bg-gradient-to-r from-[#FAFBFF] to-[#6376D2]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 2,
                    title: "Governance Built In",
                    desc: "Establish access controls, lineage, and operating standards as part of the foundation.",
                    image: "/assets/datab/item2.png",
                    cardBg: "bg-gradient-to-r from-[#F2F5F8] to-[#667386]",
                    borderClass: "border border-[#01386A45]",
                },
                {
                    id: 3,
                    title: "Cost Visibility",
                    desc: "Use tagging, budgets, and workload policies to make platform consumption easier to manage.",
                    image: "/assets/datab/item3.png",
                    cardBg: "bg-gradient-to-r from-[#F0FAFE] to-[#42B7A7]",
                    borderClass: "border border-[#01386A45]",
                },
                {
                    id: 4,
                    title: "Confident Handover",
                    desc: "Give internal teams practical documentation, runbooks, and knowledge transfer.",
                    image: "/assets/datab/item4.png",
                    cardBg: "bg-gradient-to-r from-[#F1FAFE] to-[#55A2CD]",
                    borderClass: "border border-[#01386A45]",
                },
            ],
        },
        faq: {
            badge: "Databricks consulting FAQs",
            heading: "Good questions. Clear answers.",
            subheading:
                "Everything you need to understand lakehouse architecture, data migration, and building a modern data platform.",
            items: [
                {
                    id: 1,
                    question: "What does a Databricks consulting partner do?",
                    answer:
                        "A Databricks consulting partner helps plan, implement, secure, govern, and optimize a Databricks environment—from platform architecture and data pipelines to production use cases and ongoing support.",
                },
                {
                    id: 2,
                    question: "How long does a Databricks implementation take?",
                    answer:
                        "A secure platform foundation with initial production use cases may take around 8–12 weeks, depending on scope, data readiness, integrations, and security requirements. Larger programs are usually delivered in phases.",
                },
                {
                    id: 3,
                    question: "Do we need a Databricks account before starting?",
                    answer:
                        "Not necessarily. Mobikode can help assess your cloud and account setup, estimate consumption, and plan the implementation, or work within your existing Databricks environment and agreement.",
                },
                {
                    id: 4,
                    question: "Which cloud platforms do you support?",
                    answer:
                        "The proposed service covers Databricks on AWS, Microsoft Azure, and Google Cloud. The best fit depends on your existing cloud strategy, data estate, and technical requirements.",
                },
                {
                    id: 5,
                    question: "Can you work with our existing data and engineering team?",
                    answer:
                        "Yes. The engagement can be structured around your internal data, analytics, security, and application teams, with shared delivery practices, documentation, and knowledge transfer.",
                },
                {
                    id: 6,
                    question: "Can Mobikode support the platform after launch?",
                    answer:
                        "Yes. After go-live, support can include platform administration, pipeline monitoring, enhancements, cost optimization, and ongoing operational support, based on an agreed engagement model.",
                },
            ],
        },
        banner: {
            title: "Start with a clear Databricks implementation plan.",
            text: "Book a conversation with Mobikode to assess your platform requirements, agree on the right foundations, and identify the first business use cases to take into production.",
        },
    },

    "lakehouse-architecture": {
        seo: {
            title: "Lakehouse Architecture &amp; Migration Services | Mobikode",
            description: "Modernize your data platform with Databricks lakehouse architecture and migration services. Mobikode helps plan, migrate, govern, and optimize data workloads.",
            keywords: "Databricks lakehouse architecture, lakehouse migration services, data warehouse migration, Delta Lake architecture, Databricks migration",
            canonical: "https://www.mobikode.com/data-ai-databricks-solutions/lakehouse-architecture/",
            ogImage: "https://www.mobikode.com/assets/services/bg3.png",
        },
        hero: {
            partnerTitle: "Databricks · Lakehouse Architecture & Migration",
            headingLine1: "Build a lakehouse ready",
            headingLine2: "for what comes next.",
            description: "Modernize fragmented data lakes and warehouses with a scalable Databricks lakehouse architecture and a practical, phased migration plan.",
            ctaText: "Discuss your lakehouse migration",
            ctaHref: "#databricks-lead-form",
            cardText: "Modernize a secure foundation for data engineering, analytics, and AI with an implementation plan shaped around your business priorities.",
            bgImageSrc: "/assets/datab/bg1.png",
        },
        foundation: {
            badge: "From legacy platforms to a modern lakehouse",
            title: " Unify your data architecture without losing business continuity.",
            description: "  Siloed data, duplicate pipelines, and aging warehouses make analytics harder to scale. Mobikode helps assess your current estate, design a governed Databricks lakehouse, and migrate workloads in manageable phases with data quality and business continuity in focus.",
            imageSrc: "/assets/datab/img1.png",
        },
        approach: {
            badge: " Why modernize with Mobikode",
            heading: " Lakehouse architecture that balances flexibility and control",
            items: [
                {
                    id: 1,
                    title: "A Unified Lakehouse Blueprint",
                    desc: "We bring data lakes and warehouses together with an architecture designed for analytics, AI, and reliable operations.",
                },
                {
                    id: 2,
                    title: "Governance Across Every Layer",
                    desc: "Unity Catalog, access controls, data quality, and lineage are considered throughout the lakehouse design.",
                },
                {
                    id: 3,
                    title: "Migration Without Guesswork",
                    desc: "We assess existing systems, map dependencies, and validate workloads before cutover to reduce migration risk.",
                },
                {
                    id: 4,
                    title: "Built to Evolve with Your Data",
                    desc: "Our architecture supports batch, streaming, business intelligence, and future machine learning use cases.",
                },
            ],
        },
        deliverables: {
            badge: "Architecture and migration scope",
            heading: "What your lakehouse program includes",
            description: " From target architecture and Delta Lake design to migration execution and validation, we focus on the components that make modernization practical.",
            items: [
                {
                    id: 1,
                    title: "Target Lakehouse Architecture",
                    desc: "Architect the target lakehouse, including storage layers, compute, data domains, and integration patterns.",
                },
                {
                    id: 2,
                    title: "Source-System Assessment",
                    desc: "Inventory legacy warehouses, lakes, ETL jobs, schemas, dependencies, and critical reporting workloads.",
                },
                {
                    id: 3,
                    title: "Unity Catalog and Data Governance",
                    desc: "Plan centralized permissions, metadata, lineage, and ownership for governed data access.",
                },
                {
                    id: 4,
                    title: "Delta Lake and Data Modeling",
                    desc: "Establish Delta Lake tables, medallion layers, schema management, and reusable data models.",
                },
                {
                    id: 5,
                    title: "Pipeline and Workload Migration",
                    desc: "Modernize ingestion, transformations, and analytics workloads with tested migration patterns.",
                },
                {
                    id: 6,
                    title: "Validation and Cutover Planning",
                    desc: "Reconcile data, compare performance, plan rollback, and prepare operational handover.",
                },
            ],
        },
        deliveryPath: {
            badge: "A phased modernization journey",
            heading: "How we design and migrate your lakehouse",
            subheading: "Each migration is scoped to your current systems and dependencies. We use defined phases, validation gates, and controlled cutover to reduce risk.",
            items: [
                {
                    step: "01",
                    image: "/assets/datab/img2.png",
                    title: "Assess",
                    duration: "Phase 1",
                    desc: "We assess the current data estate, workloads, technical debt, dependencies, and business priorities.",
                },
                {
                    step: "02",
                    image: "/assets/datab/img2.png",
                    title: "Architect",
                    duration: "Phase 2",
                    desc: "We define lakehouse layers, data contracts, governance, migration waves, and acceptance criteria.",
                },
                {
                    step: "03",
                    image: "/assets/datab/img2.png",
                    title: "Modernize",
                    duration: "Phase 3",
                    desc: "We implement foundational components and migrate prioritized datasets, pipelines, and workloads iteratively.",
                },
                {
                    step: "04",
                    image: "/assets/datab/img2.png",
                    title: "Validate and Cut Over",
                    duration: "Phase 4",
                    desc: "We reconcile data, test performance and security, and execute a controlled cutover with rollback planning.",
                },
                {
                    step: "05",
                    image: "/assets/datab/img2.png",
                    title: "Optimize and Enable",
                    duration: "Phase 5",
                    desc: "We tune performance and costs, document operations, and enable your teams to manage the new lakehouse.",
                },
            ],
        },
        capabilities: {
            badge: "Lakehouse modernization capabilities",
            heading: "Architecture and migration expertise",
            subheading: "Explore the workstreams behind a modern Databricks lakehouse, from ingestion and governance to warehouse migration and optimization.",
            items: [
                {
                    id: 1,
                    image: "/assets/datab/item1.png",
                    cardBg: "bg-gradient-to-r from-[#FAFBFF] to-[#6376D2]",
                    borderClass: "border-2 border-[#1E6BFF]",
                    title: "Lakehouse Reference Architecture",
                    desc: "Architect Databricks workspaces and environments for your organization, cloud platform, security model, and delivery lifecycle.",
                },
                {
                    id: 2,
                    image: "/assets/datab/item2.png",
                    cardBg: "bg-gradient-to-r from-[#F2F5F8] to-[#667386]",
                    borderClass: "border-2 border-[#1E6BFF]",
                    title: "Batch and Streaming Ingestion",
                    desc: "Ingest structured and semi-structured data through reliable batch and near-real-time pipelines.",
                },
                {
                    id: 3,
                    image: "/assets/datab/item3.png",
                    cardBg: "bg-gradient-to-r from-[#F0FAFE] to-[#42B7A7]",
                    borderClass: "border-2 border-[#1E6BFF]",
                    title: "Unity Catalog and Data Lineage",
                    desc: "Establish discoverable, controlled data assets with permissions, lineage, and stewardship.",
                },
                {
                    id: 4,
                    image: "/assets/datab/item4.png",
                    cardBg: "bg-gradient-to-r from-[#F1FAFE] to-[#55A2CD]",
                    borderClass: "border-2 border-[#1E6BFF]",
                    title: "Legacy ETL Modernization",
                    desc: "Refactor brittle legacy transformations into maintainable, testable Databricks pipelines.",
                },
                {
                    id: 5,
                    image: "/assets/datab/item1.png",
                    cardBg: "bg-gradient-to-r from-[#FAFBFF] to-[#6376D2]",
                    borderClass: "border-2 border-[#1E6BFF]",
                    title: "Warehouse and BI Migration",
                    desc: "Move selected SQL and reporting workloads while validating results and query performance.",
                },
                {
                    id: 6,
                    image: "/assets/datab/item2.png",
                    cardBg: "bg-gradient-to-r from-[#F2F5F8] to-[#667386]",
                    borderClass: "border-2 border-[#1E6BFF]",
                    title: "Performance and Cost Optimization",
                    desc: "Modernize the governed data foundations needed to support future machine learning, GenAI, and AI-agent use cases.",
                },
            ]
        },
        faq: {
            badge: "Lakehouse Knowledge Hub",
            heading: "Good questions. Clear answers.",
            subheading: " Everything you need to understand lakehouse architecture, data migration, and building a modern data platform.",
            items: [
                {
                    id: 1,
                    question: "What is a Databricks lakehouse architecture?",
                    answer:
                        "A lakehouse combines flexible data-lake storage with warehouse-style reliability, governance, and analytics capabilities. On Databricks, Delta Lake and Unity Catalog are common building blocks.",
                },
                {
                    id: 2,
                    question: "How long does a lakehouse migration take?",
                    answer:
                        "Timing depends on source systems, data volumes, pipeline complexity, governance requirements, and testing. We recommend phased migration waves rather than assuming a single fixed timeline.",
                },
                {
                    id: 3,
                    question: "Can we migrate from a traditional data warehouse?",
                    answer:
                        "Yes. We assess warehouse schemas, SQL workloads, ETL dependencies, and reporting needs, then prioritize suitable workloads for migration and validate their outputs.",
                },
                {
                    id: 4,
                    question: "Will migration disrupt existing dashboards and reports?",
                    answer:
                        "The goal is to minimize disruption through dependency mapping, parallel validation, phased cutover, and a rollback plan. Actual impact depends on the existing environment.",
                },
                {
                    id: 5,
                    question: "How do you maintain data quality during migration?",
                    answer:
                        "We define quality checks, reconcile source and target datasets, test transformations, and confirm business acceptance before production cutover.",
                },
                {
                    id: 6,
                    question: "Can our team operate the lakehouse after migration?",
                    answer:
                        "Yes. We can provide documentation, monitoring guidance, runbooks, and knowledge transfer, with optional ongoing managed support.",
                },
            ],
        },
        banner: {
            title: "Upgrade to a Modern Databricks Lakehouse.",
            text: "Speak with our cloud architects to migrate to high-performance Lakehouse foundation.",
        },
    },

    "data-engineering": {
        seo: {
            title: "Databricks Data Engineering & Governance Services | Mobikode",
            description:
                "Build reliable Databricks data pipelines with data engineering and governance services from Mobikode, covering ingestion, transformation, quality, and Unity Catalog.",
            keywords:
                "Databricks data engineering, Databricks data pipelines, data engineering services, Unity Catalog governance, ETL ELT, Delta Lake",
            canonical:
                "https://www.mobikode.com/data-ai-databricks-solutions/data-engineering/",
            ogTitle:
                "Databricks Data Engineering & Governance Services | Mobikode",
            ogDescription:
                "Develop dependable Databricks data pipelines with governed data access, quality checks, lineage, and production-ready engineering practices.",
            ogUrl:
                "https://www.mobikode.com/data-ai-databricks-solutions/data-engineering/",
            ogType: "website",
            ogSiteName: "Mobikode",
            ogImage: "https://www.mobikode.com/assets/services/bg3.png",
            twitterCard: "summary_large_image",
            twitterTitle:
                "Databricks Data Engineering & Governance Services | Mobikode",
            twitterDescription:
                "Mobikode builds and modernizes Databricks ingestion, ETL/ELT, data quality, governance, and production data pipelines.",
            twitterImage: "https://www.mobikode.com/assets/services/bg3.png",
        },
        hero: {
            partnerTitle: "Databricks · Data Engineering & Governance",
            headingLine1: "Build reliable data pipelines.",
            headingLine2: "Trust every insight.",
            description: " Design, build, and operate dependable Databricks data pipelines with governance, quality checks, and scalable engineering practices built in.",
            ctaText: "Discuss your data engineering needs",
            ctaHref: "#databricks-lead-form",
            cardText: "Turn raw data into trusted, well-governed datasets for analytics, reporting, and AI—backed by maintainable pipelines and clear operational practices.",
            bgImageSrc: "/assets/datab/bg1.png",
        },
        foundation: {
            badge: "From raw data to trusted data products",
            title: "Make your data pipelines reliable, governed, and ready to scale.",
            description: "   Disconnected sources, fragile ETL jobs, and inconsistent data quality slow down analytics. Mobikode helps you engineer reliable ingestion and transformation pipelines, establish governance, and deliver trusted datasets for business teams.",
            imageSrc: "/assets/datab/img1.png",
        },
        approach: {
            badge: " Why choose Mobikode for data engineering",
            heading: "Data engineering that balances speed, quality, and control",
            items: [
                {
                    id: 1,
                    title: "Reliable Data Pipelines from Day One",
                    desc: "Build maintainable batch and streaming pipelines that move data from source systems into trusted, business-ready datasets.",
                },
                {
                    id: 2,
                    title: "Quality and Governance Built In",
                    desc: "Apply validation, lineage, ownership, access controls, and Unity Catalog governance across the data lifecycle.",
                },
                {
                    id: 3,
                    title: "Engineering Designed for Scale",
                    desc: "Use reusable patterns, automated testing, and workload-aware engineering practices to support growing data volumes.",
                },
                {
                    id: 4,
                    title: "Production Operations You Can Trust",
                    desc: "Improve reliability with observability, failure handling, CI/CD, documentation, and clear operational ownership.",
                },
            ],
        },
        deliverables: {
            badge: "Data engineering and governance scope",
            heading: "What your data engineering program includes",
            description: "From source integration and transformation to quality checks, governance, and CI/CD, we focus on the components that keep production data dependable.",
            items: [
                {
                    id: 1,
                    title: "Data Ingestion and Integration",
                    desc: "Connect databases, APIs, files, SaaS platforms, and cloud storage through dependable ingestion patterns.",
                },
                {
                    id: 2,
                    title: "Batch and Streaming Pipelines",
                    desc: "Build scheduled and near-real-time data flows with appropriate orchestration, retries, and failure handling.",
                },
                {
                    id: 3,
                    title: "Transformation and Medallion Architecture",
                    desc: "Organize raw, validated, and curated data using Bronze, Silver, and Gold layers where they fit your requirements.",
                },
                {
                    id: 4,
                    title: "Data Quality and Observability",
                    desc: "Introduce validation rules, freshness checks, reconciliation, alerts, and monitoring for critical data assets.",
                },
                {
                    id: 5,
                    title: "Unity Catalog and Governance",
                    desc: "Set up consistent access controls, metadata, lineage, ownership, and discovery for governed data products.",
                },
                {
                    id: 6,
                    title: "CI/CD and Engineering Standards",
                    desc: "Use version control, automated tests, repeatable deployments, and documented standards to improve maintainability.",
                },
            ],
        },
        deliveryPath: {
            badge: " A practical engineering lifecycle",
            heading: "How we build production-ready data pipelines",
            subheading: "Each engagement is shaped around your data sources, service levels, and business priorities, with testing and validation built into the delivery process.",
            items: [
                {
                    step: "01",
                    duration: "Phase 1",
                    title: "Discover Data and Use Cases",
                    desc: "We review source systems, data consumers, existing pipelines, data quality issues, service expectations, and business priorities.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "02",
                    duration: "Phase 2",
                    title: "Design the Data Platform",
                    desc: "We define ingestion patterns, data layers, data contracts, governance, orchestration, quality checks, and success criteria.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "03",
                    duration: "Phase 3",
                    title: "Build Priority Pipelines",
                    desc: "We develop and test the agreed ingestion and transformation workflows using reusable engineering patterns and controlled releases.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "04",
                    duration: "Phase 4",
                    title: "Validate and Productionize",
                    desc: "We test correctness, performance, access, failure recovery, and freshness, then configure monitoring and operational runbooks.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "05",
                    duration: "Ongoing",
                    title: "Enable and Improve",
                    desc: "We transfer knowledge to your team and help improve reliability, data quality, performance, and engineering practices over time.",
                    image: "/assets/datab/img2.png",
                },
            ],
        },
        capabilities: {
            badge: "Data engineering capabilities",
            heading: " Engineering and governance expertise",
            subheading: "Explore the workstreams behind dependable Databricks data products, from ingestion and transformations to quality, governance, and DataOps.",
            items: [
                {
                    id: 1,
                    title: "Data Ingestion and Integration",
                    desc: "Create dependable ingestion from databases, APIs, enterprise applications, files, and cloud storage into Databricks.",
                    image: "/assets/datab/item1.png",
                    cardBg: "bg-gradient-to-r from-[#FAFBFF] to-[#6376D2]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 2,
                    title: "ETL and ELT Pipeline Development",
                    desc: "Transform source data into trusted datasets with maintainable logic, reusable components, and clear pipeline dependencies.",
                    image: "/assets/datab/item2.png",
                    cardBg: "bg-gradient-to-r from-[#F2F5F8] to-[#667386]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 3,
                    title: "Delta Lake and Medallion Layers",
                    desc: "Design Bronze, Silver, and Gold data layers, manage schema evolution, and create curated datasets for downstream use.",
                    image: "/assets/datab/item3.png",
                    cardBg: "bg-gradient-to-r from-[#F0FAFE] to-[#42B7A7]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 4,
                    title: "Data Quality and Observability",
                    desc: "Track freshness, completeness, validity, and pipeline health with checks, alerts, reconciliation, and monitoring.",
                    image: "/assets/datab/item4.png",
                    cardBg: "bg-gradient-to-r from-[#F1FAFE] to-[#55A2CD]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 5,
                    title: "Unity Catalog and Governance",
                    desc: "Improve discovery, controlled access, lineage, and stewardship with governance practices aligned to your organization.",
                    image: "/assets/datab/item1.png",
                    cardBg: "bg-gradient-to-r from-[#FAFBFF] to-[#6376D2]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 6,
                    title: "DataOps and Pipeline Optimization",
                    desc: "Improve release consistency, testing, orchestration, performance, and operational resilience across production workloads.",
                    image: "/assets/datab/item2.png",
                    cardBg: "bg-gradient-to-r from-[#F2F5F8] to-[#667386]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
            ],
        },
        faq: {
            badge: "Data Engineering Knowledge Hub",
            heading: "Good data starts with good engineering.",
            subheading: " Get answers about Databricks data pipelines, ETL and ELT, data quality, governance, and production-ready data engineering.",
            items: [
                {
                    id: 1,
                    question: "What is Databricks data engineering?",
                    answer: "Databricks data engineering covers the ingestion, transformation, orchestration, quality, and operational management of data pipelines on the Databricks platform. The goal is to deliver reliable, governed datasets for analytics, reporting, and AI use cases.",
                },
                {
                    id: 2,
                    question: "Can you build both batch and streaming pipelines?",
                    answer: "Yes. Pipeline design can support scheduled batch processing and near-real-time streaming, depending on source capabilities, latency requirements, data volumes, and operational constraints.",
                },
                {
                    id: 3,
                    question: "How do you maintain data quality?",
                    answer: "We define quality rules with business and technical stakeholders, add validation and reconciliation checks, monitor freshness and failures, and make issues visible through alerts and operational reporting.",
                },
                {
                    id: 4,
                    question: "What is the role of Unity Catalog in data engineering?",
                    answer: "Unity Catalog can provide centralized governance for data and other supported assets, including permissions, discovery, and lineage. The exact setup depends on your platform architecture and governance needs.",
                },
                {
                    id: 5,
                    question: "Can you modernize our existing ETL pipelines?",
                    answer: "Yes. We can assess existing jobs, transformation logic, dependencies, and service requirements, then prioritize pipelines for refactoring or migration with testing and output validation.",
                },
                {
                    id: 6,
                    question: "Do you provide support after deployment?",
                    answer: "Yes. Depending on the engagement, support can include pipeline monitoring, incident troubleshooting, performance tuning, enhancements, documentation, and knowledge transfer to your team.",
                },
            ],
        },
        banner: {
            title: "Build reliable Databricks data pipelines with confidence.",
            text: "Book a conversation with Mobikode to assess your pipeline architecture, modernize legacy ETL workflows, and deliver trusted datasets to your business.",
        },
    },
    "generative-ai-agent-bricks": {
        seo: {
            title: "Generative AI & AI Agents on Databricks | Mobikode",
            description:
                "Build production-ready GenAI assistants and AI agents on governed enterprise data with Databricks Agent Bricks, MLflow, and Unity AI Gateway from Mobikode.",
            keywords:
                "Databricks generative AI services, Agent Bricks consulting, AI agents on Databricks, RAG development, Mosaic AI, enterprise GenAI",
            canonical:
                "https://www.mobikode.com/data-ai-databricks-solutions/generative-ai-agent-bricks/",
            ogTitle:
                "Generative AI & AI Agents on Databricks | Mobikode",
            ogDescription:
                "Build trusted GenAI assistants and enterprise AI agents with Databricks Agent Bricks, MLflow, and Unity AI Gateway.",
            ogUrl:
                "https://www.mobikode.com/data-ai-databricks-solutions/generative-ai-agent-bricks/",
            ogType: "website",
            ogSiteName: "Mobikode",
            ogImage: "https://www.mobikode.com/assets/services/bg3.png",
            twitterCard: "summary_large_image",
            twitterTitle:
                "Generative AI & AI Agents on Databricks | Mobikode",
            twitterDescription:
                "Mobikode builds production GenAI assistants and AI agents grounded in governed enterprise data, with evaluation, guardrails, and monitoring.",
            twitterImage: "https://www.mobikode.com/assets/services/bg3.png",
        },
        hero: {
            partnerTitle: "Databricks Partner · GenAI & AI Agents",
            headingLine1: "AI agents that work on ",
            headingLine2: "your data, not just in demos.",
            description:
                "We build GenAI assistants, machine learning models, and AI agents on Databricks, grounded in your governed data and measured before they go live.",
            ctaText: "Start a GenAI pilot",
            ctaHref: "#databricks-lead-form",
            cardText:
                "Move from AI demos to measurable business results with governed data, clear success measures, and production-ready controls.",
            bgImageSrc: "/assets/datab/bg1.png",
        },
        foundation: {
            badge: "From AI experiments to enterprise outcomes",
            title: "Build AI assistants and agents your teams can trust.",
            description: "AI pilots often stall when value is unclear, data is not ready, or risk controls arrive too late. Mobikode helps you select useful use cases, ground AI in governed enterprise knowledge, evaluate quality, and build guardrails into the delivery process.",
            imageSrc: "/assets/datab/img1.png",
        },
        approach: {
            badge: "Why choose Mobikode for enterprise GenAI",
            heading: "Move beyond demos with measurable, governed AI",
            items: [
                {
                    id: 1,
                    title: "AI Built Around Real Business Outcomes",
                    desc: "Select high-value, low-risk use cases with clear success measures so pilots solve meaningful problems instead of stopping at demos.",
                },
                {
                    id: 2,
                    title: "Grounded in Your Governed Data",
                    desc: "Build assistants and agents around trusted enterprise knowledge, with relevant source content and access controls in place.",
                },
                {
                    id: 3,
                    title: "Guardrails from the First Sprint",
                    desc: "Include evaluation, content safety, personal-data filtering, approval steps, and controlled tool access throughout development.",
                },
                {
                    id: 4,
                    title: "Designed for Production",
                    desc: "Prepare for real users with traceability, monitoring, load testing, human review, and ongoing quality improvement.",
                },
            ],
        },
        deliverables: {
            badge: "GenAI and AI agent delivery scope",
            heading: "What your GenAI program can include",
            description: "From knowledge assistants and document intelligence to agents, evaluation, governance, and monitoring, we focus on the components required for production AI.",
            items: [
                {
                    id: 1,
                    title: "Knowledge Assistants and RAG",
                    desc: "Answer questions using policies, manuals, contracts, and other approved enterprise content, with sources surfaced for review.",
                },
                {
                    id: 2,
                    title: "Document Intelligence",
                    desc: "Extract, classify, and summarize information from invoices, claims, reports, forms, and business email.",
                },
                {
                    id: 3,
                    title: "AI Agents and Workflow Automation",
                    desc: "Build multi-step agents that use tools and business systems to support practical, governed workflows.",
                },
                {
                    id: 4,
                    title: "Machine Learning and MLOps",
                    desc: "Develop forecasting, risk, and recommendation models with MLflow, feature tables, evaluation, and monitoring.",
                },
                {
                    id: 5,
                    title: "Conversational Analytics",
                    desc: "Help teams ask plain-English questions about governed business data using Databricks Genie.",
                },
                {
                    id: 6,
                    title: "AI-Powered Business Applications",
                    desc: "Put AI experiences in front of business users through applications designed around specific tasks and workflows.",
                },
            ],
        },
        deliveryPath: {
            badge: "A practical path to production AI",
            heading: "How we take AI from pilot to production",
            subheading: "Each engagement is shaped around your use case, users, data readiness, and risk profile, with evaluation and safety controls built into the delivery process.",
            items: [
                {
                    step: "01",
                    duration: "Phase 1",
                    title: "Choose the Right Use Case",
                    desc: "Run a use-case workshop to identify valuable, feasible opportunities, define intended users, and agree on measurable success criteria.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "02",
                    duration: "Phase 2",
                    title: "Prepare Data and Knowledge",
                    desc: "Review source content, permissions, data readiness, retrieval needs, and the systems the assistant or agent must access.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "03",
                    duration: "2–4 Weeks",
                    title: "Build a Working Pilot",
                    desc: "Develop an initial assistant, model, or agent and establish a quality baseline using representative evaluation examples.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "04",
                    duration: "Phase 4",
                    title: "Harden for Enterprise Use",
                    desc: "Add guardrails, security review, load testing, human-in-the-loop controls, and traceability before production release.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "05",
                    duration: "Ongoing",
                    title: "Launch, Monitor, Improve",
                    desc: "Deploy with monitoring and evaluation, review performance with stakeholders, and continuously improve quality, cost, and usefulness.",
                    image: "/assets/datab/img2.png",
                },
            ],
        },
        capabilities: {
            badge: "GenAI and AI agent capabilities",
            heading: "Enterprise AI expertise",
            subheading: "Explore the workstreams behind production GenAI, from retrieval and agents to evaluation, governance, and monitoring.",
            items: [
                {
                    id: 1,
                    title: "RAG and Knowledge Assistants",
                    desc: "Ground responses in approved enterprise documents and knowledge sources, with relevant source references to help users validate answers.",
                    image: "/assets/home-/ai-2.png",
                    cardBg: "bg-gradient-to-r from-[#FAFBFF] to-[#6376D2]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 2,
                    title: "Agent Bricks and AI Agents",
                    desc: "Create multi-step agents that can use tools, maintain task context, and interact with business systems within defined permissions.",
                    image: "/assets/home-/ai-3.png",
                    cardBg: "bg-gradient-to-r from-[#F2F5F8] to-[#667386]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 3,
                    title: "Document Intelligence",
                    desc: "Turn unstructured documents into usable information with extraction, classification, summarization, and validation workflows.",
                    image: "/assets/home-/ai-4.png",
                    cardBg: "bg-gradient-to-r from-[#F0FAFE] to-[#42B7A7]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 4,
                    title: "Evaluation and Guardrails",
                    desc: "Measure accuracy, safety, and response speed; filter sensitive data; and add approval steps for consequential actions.",
                    image: "/assets/home-/img1.png",
                    cardBg: "bg-gradient-to-r from-[#F1FAFE] to-[#55A2CD]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 5,
                    title: "Unity AI Gateway and Governance",
                    desc: "Manage model access, rate limits, and spend controls while supporting governed access to enterprise data and AI services.",
                    image: "/assets/home-/img2.png",
                    cardBg: "bg-gradient-to-r from-[#FAFBFF] to-[#6376D2]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 6,
                    title: "MLflow and Production Monitoring",
                    desc: "Track prompts, tool calls, decisions, and model quality to support traceability, troubleshooting, and continuous improvement.",
                    image: "/assets/home-/img3.png",
                    cardBg: "bg-gradient-to-r from-[#F2F5F8] to-[#667386]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
            ],
        },
        faq: {
            badge: "GenAI & AI Agents Knowledge Hub",
            heading: "Trusted AI starts with the right foundations.",
            subheading: "Get answers about Databricks Agent Bricks, RAG, enterprise AI safety, and production-ready AI agents.",
            items: [
                {
                    id: 1,
                    question: "What is Databricks Agent Bricks?",
                    answer: "Databricks Agent Bricks is a platform capability for building, evaluating, deploying, and governing AI agents on enterprise data. The right implementation depends on your use case, data readiness, and security requirements.",
                },
                {
                    id: 2,
                    question: "What is retrieval-augmented generation (RAG)?",
                    answer: "RAG retrieves relevant information from your own documents or knowledge sources and provides it to an AI model as context, helping answers reflect your organization's information rather than relying only on general model knowledge.",
                },
                {
                    id: 3,
                    question: "Is our enterprise data safe?",
                    answer: "We design for governed data access, model controls, sensitive-data filtering, and approval steps where needed. Specific safeguards are defined during architecture and security review to match your environment and requirements.",
                },
                {
                    id: 4,
                    question: "How quickly can we see results?",
                    answer: "A working pilot can typically be developed in 2–4 weeks. Production launch follows additional evaluation, security hardening, load testing, and operational readiness work.",
                },
                {
                    id: 5,
                    question: "Can AI agents connect to our business systems?",
                    answer: "Yes. Agents can be designed to work with approved tools and enterprise workflows, including systems such as ERP, CRM, and ServiceNow, with permissions and human approval controls appropriate to each action.",
                },
                {
                    id: 6,
                    question: "How do you measure AI quality?",
                    answer: "We work with your subject-matter experts to create evaluation examples and measure dimensions such as accuracy, safety, and speed. Monitoring and traceability help teams investigate issues and improve releases over time.",
                },
            ],
        },
        banner: {
            title: "Start building governed AI agents on Databricks.",
            text: "Schedule an architecture session with Mobikode to identify your priority GenAI use cases, build working pilots, and safely deploy production agents on enterprise data.",
        },
    },
    "databricks-sql-bi": {
        seo: {
            title: "Databricks Analytics, BI & Dashboard Services | Mobikode",
            description:
                "Build fast, trusted analytics on Databricks SQL with Power BI, Tableau, AI/BI dashboards, and Genie for plain-English business questions.",
            keywords:
                "Databricks analytics and BI services, Databricks SQL, Power BI consulting, Tableau development, Databricks Genie, KPI dashboards, self-service analytics",
            canonical:
                "https://www.mobikode.com/data-ai-databricks-solutions/databricks-sql-bi/",
            ogTitle:
                "Databricks Analytics, BI & Dashboard Services | Mobikode",
            ogDescription:
                "Deliver fast, trusted Databricks analytics with Power BI, Tableau, AI/BI dashboards, and conversational analytics.",
            ogUrl:
                "https://www.mobikode.com/data-ai-databricks-solutions/databricks-sql-bi/",
            ogType: "website",
            ogSiteName: "Mobikode",
            ogImage: "https://www.mobikode.com/assets/services/bg3.png",
            twitterCard: "summary_large_image",
            twitterTitle:
                "Databricks Analytics, BI & Dashboard Services | Mobikode",
            twitterDescription:
                "Mobikode delivers trusted Databricks SQL analytics, KPI dashboards, Power BI and Tableau integration, and conversational analytics.",
            twitterImage: "https://www.mobikode.com/assets/services/bg3.png",
        },
        hero: {
            partnerTitle: "Databricks Partner · Analytics & BI",
            headingLine1: "One version of the truth,",
            headingLine2: "at the speed of business.",
            description:
                "We turn your Databricks lakehouse into fast, trusted analytics, with dashboards people actually use and answers in plain English.",
            ctaText: "Get a free dashboard prototype",
            ctaHref: "#databricks-lead-form",
            cardText:
                "Get consistent metrics, faster dashboards, and clear answers from your governed business data.",
            bgImageSrc: "/assets/datab/bg1.png",
        },
        foundation: {
            badge: "From scattered reports to trusted decisions",
            title: "Give every team a reliable view of business performance.",
            description:
                "When teams use different definitions, reports refresh slowly, and dashboards go unused, the problem is often inconsistent metrics and inefficient queries. Mobikode helps you build governed analytics on Databricks SQL so teams can work from the same trusted numbers.",
            imageSrc: "/assets/datab/img1.png",
        },
        approach: {
            badge: "Why choose Mobikode for analytics and BI",
            heading: "Turn trusted data into confident business decisions",
            items: [
                {
                    id: 1,
                    title: "Decision-First Dashboard Design",
                    desc: "Start with the business decisions each dashboard must support, then define the questions, audiences, and actions it should make easier.",
                },
                {
                    id: 2,
                    title: "One Trusted Set of Metrics",
                    desc: "Agree on KPI definitions with business owners and publish certified metrics from governed, business-ready gold tables.",
                },
                {
                    id: 3,
                    title: "Built on Governed Data",
                    desc: "Connect analytics to trusted Databricks data with Unity Catalog permissions and row-level security for appropriate access.",
                },
                {
                    id: 4,
                    title: "Performance and Adoption Together",
                    desc: "Tune SQL workloads, train users, and track dashboard usage and cost so analytics stays fast and useful after launch.",
                },
            ],
        },
        deliverables: {
            badge: "Analytics and BI delivery scope",
            heading: "What your analytics program can include",
            description:
                "From SQL warehouses and certified KPIs to dashboards, Genie, and performance tuning, we build analytics around the decisions your teams need to make.",
            items: [
                {
                    id: 1,
                    title: "Databricks SQL Warehousing",
                    desc: "Configure serverless SQL warehouses sized and isolated for BI workloads, reporting, and ad hoc analysis.",
                },
                {
                    id: 2,
                    title: "Certified Business Metrics",
                    desc: "Create business-ready gold tables and KPI definitions agreed with data owners, giving teams a consistent source of truth.",
                },
                {
                    id: 3,
                    title: "Power BI, Tableau, and AI/BI Dashboards",
                    desc: "Build executive and operational dashboards around real decisions, clear visual hierarchy, and dependable data.",
                },
                {
                    id: 4,
                    title: "Conversational Analytics with Genie",
                    desc: "Configure Databricks Genie spaces around business terminology so users can ask plain-English questions of governed data.",
                },
                {
                    id: 5,
                    title: "Embedded Analytics",
                    desc: "Bring trusted dashboards and analytics into your customer portals, internal tools, and business applications.",
                },
                {
                    id: 6,
                    title: "Performance Tuning and Migration",
                    desc: "Optimize Databricks SQL queries, migrate legacy reports, and retire duplicate dashboards where they no longer add value.",
                },
            ],
        },
        deliveryPath: {
            badge: "A practical path to trusted analytics",
            heading: "How we deliver analytics people can trust",
            subheading:
                "Each engagement is shaped around your use case, users, data readiness, and risk profile, with evaluation and safety controls built into the delivery process.",
            items: [
                {
                    step: "01",
                    duration: "Phase 1",
                    title: "Understand Decisions and Users",
                    desc: "Interview business users and stakeholders to identify the decisions each report supports, the audiences involved, and the questions they need answered.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "02",
                    duration: "Phase 2",
                    title: "Define and Certify KPIs",
                    desc: "Agree on metric definitions, calculation rules, data owners, freshness expectations, and access requirements before building dashboards.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "03",
                    duration: "Rapid Prototype",
                    title: "Prototype on Real Data",
                    desc: "Create an early dashboard or report using representative data so users can validate the layout, metrics, and decision flow.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "04",
                    duration: "Phase 4",
                    title: "Build Secure, Fast Analytics",
                    desc: "Connect BI tools to governed Databricks data, apply row-level security, and tune SQL queries and warehouse settings.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "05",
                    duration: "Ongoing",
                    title: "Launch and Improve Adoption",
                    desc: "Train users, publish clear definitions, and monitor usage, refresh performance, and cost to keep analytics valuable over time.",
                    image: "/assets/datab/img2.png",
                },
            ],
        },
        capabilities: {
            badge: "Analytics and BI capabilities",
            heading: "Databricks analytics expertise",
            subheading:
                "Explore the building blocks behind trusted BI, from Databricks SQL and dashboards to Genie, governance, and performance optimization.",
            items: [
                {
                    id: 1,
                    title: "Databricks SQL Warehouses",
                    desc: "Design SQL warehouse configurations for business intelligence and ad hoc analytics, balancing responsiveness, workload isolation, and cost.",
                    image: "/assets/home-/ai-2.png",
                    cardBg: "bg-gradient-to-r from-[#FAFBFF] to-[#6376D2]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 2,
                    title: "Power BI and Tableau",
                    desc: "Connect the BI tools your teams already use to Databricks SQL, with well-defined models and optimized queries.",
                    image: "/assets/home-/ai-3.png",
                    cardBg: "bg-gradient-to-r from-[#F2F5F8] to-[#667386]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 3,
                    title: "Databricks AI/BI Dashboards",
                    desc: "Build clear executive and operational views that put important KPIs, trends, and exceptions in context.",
                    image: "/assets/home-/ai-4.png",
                    cardBg: "bg-gradient-to-r from-[#F0FAFE] to-[#42B7A7]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 4,
                    title: "Genie and Conversational Analytics",
                    desc: "Tune Databricks Genie around your business vocabulary and governed data so people can ask questions in plain English.",
                    image: "/assets/home-/img1.png",
                    cardBg: "bg-gradient-to-r from-[#F1FAFE] to-[#55A2CD]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 5,
                    title: "KPI Modeling and Governance",
                    desc: "Create certified metrics and apply Unity Catalog permissions and row-level security so users see consistent, appropriate data.",
                    image: "/assets/home-/img2.png",
                    cardBg: "bg-gradient-to-r from-[#FAFBFF] to-[#6376D2]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 6,
                    title: "SQL Performance and Cost",
                    desc: "Review query patterns, warehouse sizing, and workload behavior to improve response times and manage analytics spend.",
                    image: "/assets/home-/img3.png",
                    cardBg: "bg-gradient-to-r from-[#F2F5F8] to-[#667386]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
            ],
        },
        faq: {
            badge: "Analytics & BI Knowledge Hub",
            heading: "Trusted analytics starts with the right definitions.",
            subheading:
                "Get answers about Databricks SQL, Power BI, Tableau, Genie, KPI definitions, and secure business dashboards.",
            items: [
                {
                    id: 1,
                    question: "Can Databricks replace our data warehouse?",
                    answer:
                        "For many BI workloads, Databricks SQL can provide warehouse-style analytics on open lakehouse tables. The right approach depends on your workloads, architecture, performance requirements, and existing platform investments.",
                },
                {
                    id: 2,
                    question: "Do we have to give up Power BI or Tableau?",
                    answer:
                        "No. We can connect your existing Power BI or Tableau environment to Databricks SQL and optimize the data models, queries, and access patterns around it.",
                },
                {
                    id: 3,
                    question: "What is Databricks Genie?",
                    answer:
                        "Databricks Genie enables business users to ask questions in plain English and explore answers from governed data. Results depend on the quality of the underlying data, semantic setup, and business definitions.",
                },
                {
                    id: 4,
                    question: "How do you keep dashboard data secure?",
                    answer:
                        "We design access around Unity Catalog permissions and row-level security where appropriate, so users can access only the data authorized for their role and responsibilities.",
                },
                {
                    id: 5,
                    question: "Can you migrate our existing reports?",
                    answer:
                        "Yes. We can assess legacy reports and warehouse dependencies, prioritize migration, rebuild or repoint dashboards, and reconcile key metrics before cutover.",
                },
                {
                    id: 6,
                    question: "How do you improve dashboard performance?",
                    answer:
                        "We review SQL queries, data models, warehouse sizing, refresh patterns, and dashboard design to identify bottlenecks and balance response time with cost.",
                },
            ],
        },
        banner: {
            title: "Make your business data fast, visible, and trusted.",
            text: "Schedule an analytics consultation with Mobikode to connect your BI tools to Databricks SQL, certify key metrics, and configure conversational analytics.",
        },
    },
    "databricks-managed-services": {
        seo: {
            title: "Databricks Managed Services & Cost Optimization | Mobikode",
            description:
                "24x7 Databricks operations and FinOps from a Databricks Partner. Keep pipelines, models and agents reliable while optimizing platform spend.",
            keywords:
                "Databricks managed services, Databricks support, Databricks cost optimization, Databricks FinOps, platform operations",
            canonical:
                "https://www.mobikode.com/data-ai-databricks-solutions/databricks-managed-services/",
            ogTitle:
                "Databricks Managed Services & Cost Optimization | Mobikode",
            ogDescription:
                "Run and optimize Databricks with platform monitoring, operational support, security practices, and cost management.",
            ogUrl:
                "https://www.mobikode.com/data-ai-databricks-solutions/databricks-managed-services/",
            ogType: "website",
            ogSiteName: "Mobikode",
            ogImage: "https://www.mobikode.com/assets/services/bg3.png",
            twitterCard: "summary_large_image",
            twitterTitle:
                "Databricks Managed Services & Cost Optimization | Mobikode",
            twitterDescription:
                "Mobikode supports Databricks reliability, platform administration, pipeline operations, and ongoing FinOps.",
            twitterImage: "https://www.mobikode.com/assets/services/bg3.png",
        },
        hero: {
            partnerTitle: "Databricks Partner · Managed Services",
            headingLine1: "Your Databricks platform,",
            headingLine2: "run like it matters.",
            description:
                "We run, monitor, and optimize Databricks so your data and AI stay reliable while platform costs remain visible and controlled.",
            ctaText: "Get a free cost review",
            ctaHref: "#databricks-lead-form",
            cardText:
                "Get dependable operations, clearer service ownership, and a practical plan for improving platform reliability and spend.",
            bgImageSrc: "/assets/datab/bg1.png",
        },
        foundation: {
            badge: "From go-live to reliable operations",
            title: "Keep your Databricks platform reliable, secure, and cost-aware.",
            description:
                "After go-live, failed jobs, access requests, upgrades, and rising consumption can put pressure on internal teams. Mobikode provides structured operational support, monitoring, platform administration, and continuous cost optimization based on agreed service levels.",
            imageSrc: "/assets/datab/img1.png",
        },
        approach: {
            badge: "Why choose Mobikode for Databricks managed services",
            heading: "Keep workloads dependable and costs under control",
            items: [
                {
                    id: 1,
                    title: "Operational Reliability",
                    desc: "Monitor jobs, pipelines, models, and platform health with clear alerting, ownership, and agreed response processes.",
                },
                {
                    id: 2,
                    title: "Cost Visibility and FinOps",
                    desc: "Understand spend by workspace, team, job, and user, then prioritize practical optimization opportunities.",
                },
                {
                    id: 3,
                    title: "Secure Platform Administration",
                    desc: "Manage workspaces, permissions, cluster policies, and Unity Catalog controls with consistent operational practices.",
                },
                {
                    id: 4,
                    title: "Continuous Improvement",
                    desc: "Review service performance, incidents, consumption, and enhancement priorities in transparent monthly reports.",
                },
            ],
        },
        deliverables: {
            badge: "Managed services coverage",
            heading: "What your managed services plan can include",
            description:
                "From monitoring and platform administration to pipeline operations, security reviews, and FinOps, support is shaped around your workloads and service requirements.",
            items: [
                {
                    id: 1,
                    title: "24x7 Monitoring and Support",
                    desc: "Monitor job failures, missed deadlines, platform incidents, and operational alerts against agreed service levels and response times.",
                },
                {
                    id: 2,
                    title: "Platform Administration",
                    desc: "Support workspaces, users, access requests, cluster policies, Unity Catalog configuration, and routine platform maintenance.",
                },
                {
                    id: 3,
                    title: "Pipeline and Model Operations",
                    desc: "Coordinate reruns and fixes, monitor data freshness, and track model health and drift where applicable.",
                },
                {
                    id: 4,
                    title: "Release and Security Operations",
                    desc: "Plan runtime upgrades, review access, monitor audit logs, and help remediate operational security issues.",
                },
                {
                    id: 5,
                    title: "Databricks Cost Optimization",
                    desc: "Review compute and SQL warehouse sizing, serverless adoption, auto-termination, tagging, budgets, and query efficiency.",
                },
                {
                    id: 6,
                    title: "Enhancements and Expert Capacity",
                    desc: "Provide ongoing capacity for new pipelines, dashboards, platform improvements, and approved AI use cases.",
                },
            ],
        },
        deliveryPath: {
            badge: "A structured path to reliable operations",
            heading: "How we manage and improve your Databricks platform",
            subheading:
                "Each engagement starts with a clear operational baseline, agreed service levels, and a transition plan, then moves into monitoring, incident response, reporting, and continuous improvement.",
            items: [
                {
                    step: "01",
                    duration: "Phase 1",
                    title: "Assess Platform and Priorities",
                    desc: "Review architecture, workloads, access controls, incidents, service expectations, and current consumption to establish a practical operating baseline.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "02",
                    duration: "Phase 2",
                    title: "Agree on Service Levels",
                    desc: "Define coverage, severity levels, response and escalation paths, responsibilities, reporting, and change-management practices.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "03",
                    duration: "Transition",
                    title: "Stabilize Operations",
                    desc: "Set up monitoring, alerts, runbooks, access workflows, backup and recovery considerations, and handover from existing teams.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "04",
                    duration: "Ongoing",
                    title: "Operate and Optimize",
                    desc: "Manage incidents and routine administration while reviewing spend, reliability, security, and platform performance.",
                    image: "/assets/datab/img2.png",
                },
                {
                    step: "05",
                    duration: "Monthly",
                    title: "Report and Improve",
                    desc: "Share service metrics, root causes, cost trends, completed improvements, and a prioritized plan for the next period.",
                    image: "/assets/datab/img2.png",
                },
            ],
        },
        capabilities: {
            badge: "Managed services capabilities",
            heading: "Databricks operations and FinOps expertise",
            subheading:
                "Explore the operational building blocks behind a dependable Databricks platform, from monitoring and access administration to cost optimization and service reporting.",
            items: [
                {
                    id: 1,
                    title: "Platform Monitoring and Incident Response",
                    desc: "Track job failures, missed SLAs, workload health, and operational signals with clear ownership, escalation, and recovery runbooks.",
                    image: "/assets/home-/ai-2.png",
                    cardBg: "bg-gradient-to-r from-[#FAFBFF] to-[#6376D2]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 2,
                    title: "Workspace and Access Administration",
                    desc: "Manage workspace settings, users, groups, cluster policies, and governed data access using consistent operational controls.",
                    image: "/assets/home-/ai-3.png",
                    cardBg: "bg-gradient-to-r from-[#F2F5F8] to-[#667386]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 3,
                    title: "Pipeline and Model Operations",
                    desc: "Support reliable pipeline execution, data freshness checks, model monitoring, and timely investigation of recurring issues.",
                    image: "/assets/home-/ai-4.png",
                    cardBg: "bg-gradient-to-r from-[#F0FAFE] to-[#42B7A7]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 4,
                    title: "FinOps and Cost Optimization",
                    desc: "Analyze consumption patterns, right-size compute and SQL warehouses, and identify opportunities to reduce avoidable spend.",
                    image: "/assets/home-/img1.png",
                    cardBg: "bg-gradient-to-r from-[#F1FAFE] to-[#55A2CD]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 5,
                    title: "Release and Security Management",
                    desc: "Plan supported runtime upgrades, review audit signals, coordinate access reviews, and help teams adopt platform changes safely.",
                    image: "/assets/home-/img2.png",
                    cardBg: "bg-gradient-to-r from-[#FAFBFF] to-[#6376D2]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
                {
                    id: 6,
                    title: "Enhancements and Service Reporting",
                    desc: "Extend platform capabilities while reporting reliability, incident trends, consumption, and continuous-improvement actions.",
                    image: "/assets/home-/img3.png",
                    cardBg: "bg-gradient-to-r from-[#F2F5F8] to-[#667386]",
                    borderClass: "border-2 border-[#1E6BFF]",
                },
            ],
        },
        faq: {
            badge: "Databricks Managed Services Knowledge Hub",
            heading: "Reliable operations start with clear ownership and visibility.",
            subheading:
                "Get answers about Databricks support, onboarding, pipeline operations, platform security, and FinOps.",
            items: [
                {
                    id: 1,
                    question: "What are Databricks managed services?",
                    answer:
                        "Databricks managed services provide ongoing operational support for your platform, which can include monitoring, incident response, workspace administration, pipeline operations, security reviews, and cost optimization based on the agreed scope.",
                },
                {
                    id: 2,
                    question: "Do you provide 24x7 support?",
                    answer:
                        "Monitoring and support coverage can be structured around your operational needs and agreed service levels. Coverage hours, response targets, escalation paths, and responsibilities should be defined during onboarding.",
                },
                {
                    id: 3,
                    question: "Can you help reduce our Databricks costs?",
                    answer:
                        "Yes. We review workload consumption, compute and SQL warehouse sizing, serverless options, auto-termination, query efficiency, tagging, and budgets to identify and prioritize cost improvements. Savings depend on your current setup and workload patterns.",
                },
                {
                    id: 4,
                    question: "Will you manage our existing pipelines and models?",
                    answer:
                        "We can support existing pipelines and model operations, including monitoring, troubleshooting, reruns, data freshness checks, and agreed maintenance activities after reviewing your architecture and runbooks.",
                },
                {
                    id: 5,
                    question: "How does onboarding work?",
                    answer:
                        "Onboarding typically starts with a platform and workload assessment, followed by agreement on service levels, access and escalation procedures, monitoring, runbooks, and a structured handover from your current team.",
                },
                {
                    id: 6,
                    question: "Can Mobikode support the platform after launch?",
                    answer:
                        "Yes. Ongoing support can include platform administration, pipeline monitoring, enhancements, cost optimization, and operational reporting based on an agreed engagement model.",
                },
            ],
        },
        banner: {
            title: "Ensure reliable, cost-efficient Databricks operations.",
            text: "Partner with Mobikode for 24x7 monitoring, expert administration, proactive FinOps, and continuous improvement across your platform.",
        },
    }
};